import { getDocumentBundleDir } from "../../lib/publish-utils";
import { getPlatformService } from "../../platform";
import type { AnnotationDocument, AnnotationStatus, PreviewAnnotation } from "./types";

export const ANNOTATION_PACKAGE_NAME = "annotations";
export const ANNOTATION_INDEX_FILE_NAME = "index.json";

export function getAnnotationDir(documentPath: string): string {
    return `${getDocumentBundleDir(documentPath)}/${ANNOTATION_PACKAGE_NAME}`;
}

export function getAnnotationIndexPath(documentPath: string): string {
    return `${getAnnotationDir(documentPath)}/${ANNOTATION_INDEX_FILE_NAME}`;
}

export function createEmptyAnnotationDocument(
    documentPath: string,
    updatedAt = new Date().toISOString(),
): AnnotationDocument {
    return {
        version: 1,
        kind: "magic-md.annotations",
        documentPath,
        updatedAt,
        annotations: [],
    };
}

export async function loadAnnotationDocument(
    documentPath: string,
): Promise<AnnotationDocument> {
    try {
        const { content } = await getPlatformService().engine.workspaceReadFile({
            path: getAnnotationIndexPath(documentPath),
        });
        return normalizeAnnotationDocument(JSON.parse(content), documentPath);
    } catch {
        return createEmptyAnnotationDocument(documentPath);
    }
}

export async function saveAnnotationDocument(
    documentPath: string,
    annotations: PreviewAnnotation[],
): Promise<AnnotationDocument> {
    const updatedAt = new Date().toISOString();
    const document: AnnotationDocument = {
        version: 1,
        kind: "magic-md.annotations",
        documentPath,
        updatedAt,
        annotations,
    };
    const engine = getPlatformService().engine;
    await engine.workspaceMkdir({ path: getAnnotationDir(documentPath), recursive: true });
    await engine.workspaceWriteFile({
        path: getAnnotationIndexPath(documentPath),
        content: `${JSON.stringify(document, null, 2)}\n`,
        createParents: true,
    });
    return document;
}

export function countAnnotationsByStatus(
    annotations: PreviewAnnotation[],
): Record<AnnotationStatus, number> {
    return annotations.reduce<Record<AnnotationStatus, number>>(
        (acc, annotation) => {
            acc[annotation.status] += 1;
            return acc;
        },
        { open: 0, resolved: 0, orphaned: 0 },
    );
}

function normalizeAnnotationDocument(
    raw: unknown,
    documentPath: string,
): AnnotationDocument {
    if (!raw || typeof raw !== "object") {
        return createEmptyAnnotationDocument(documentPath);
    }
    const input = raw as Partial<AnnotationDocument>;
    const annotations = Array.isArray(input.annotations)
        ? input.annotations.filter(isPreviewAnnotation)
        : [];
    return {
        version: 1,
        kind: "magic-md.annotations",
        documentPath: typeof input.documentPath === "string" ? input.documentPath : documentPath,
        updatedAt: typeof input.updatedAt === "string"
            ? input.updatedAt
            : new Date().toISOString(),
        annotations,
    };
}

function isPreviewAnnotation(value: unknown): value is PreviewAnnotation {
    if (!value || typeof value !== "object") return false;
    const annotation = value as Partial<PreviewAnnotation>;
    return (
        typeof annotation.id === "string" &&
        (annotation.status === "open" ||
            annotation.status === "resolved" ||
            annotation.status === "orphaned") &&
        !!annotation.target &&
        typeof annotation.target === "object" &&
        !!annotation.style &&
        typeof annotation.style === "object"
    );
}
